import { Listbox, Transition } from '@headlessui/react';
import { DropDownChevronDownIcon } from 'assets/icons';
import classnames from 'classnames';
import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { createPortal } from 'react-dom';

interface ListDropDownProps {
  className?: string;
  selectedItem: any;
  items: any[];
  setSelectedItem: (item: any) => void;
  hideDropDownIcon?: boolean;
  buttonStyles?: string;
  optionStyles?: string;
  wrapperStyles?: string;
  disabled?: boolean;
  onClick?: () => void;
  spanStyles?: string;
  optionsHeight?: string;
  placeholder?: string;
  itemStyle?: string;
  id: string;
  iconStyle?: string;
  emptyStateMessage?: string;
  prefixIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  spanComponent?: (item: string) => React.ReactNode;
  isPortal?: boolean;
  optionComponent?: (item: any) => React.ReactNode;
}

const ListDropDown: FC<ListDropDownProps> = ({
  wrapperStyles,
  optionStyles,
  buttonStyles,
  selectedItem,
  setSelectedItem,
  items,
  disabled = false,
  onClick = null,
  spanStyles = '',
  optionsHeight = '',
  hideDropDownIcon = false,
  placeholder = '',
  itemStyle = '',
  id = 'list-dropdown',
  iconStyle = '',
  emptyStateMessage = 'No items found',
  prefixIcon,
  trailingIcon,
  spanComponent = null,
  isPortal = false,
  optionComponent = null
}) => {
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isPortal) return;

    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        if (rect.width > 0) {
          setButtonPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width
          });
        }
      }
    };

    const resizeObserver = new ResizeObserver(updatePosition);
    if (buttonRef.current) {
      resizeObserver.observe(buttonRef.current);
    }

    window.addEventListener('scroll', updatePosition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', updatePosition);
    };
  }, [isPortal]);

  const renderOptions = () => {
    const options = (
      <Listbox.Options
        className={twMerge(
          `absolute z-[60] mt-[2px] ${optionsHeight !== '' ? `${optionsHeight} overflow-auto` : 'h-fit'}
          w-full min-w-[200px] rounded-md border
          border-borderGray bg-primary
          px-4 shadow-md sm:px-0`,
          classnames({ [optionStyles]: optionStyles })
        )}
        style={
          isPortal
            ? {
                top: `${buttonPosition.top}px`,
                left: `${buttonPosition.left}px`,
                width: `${buttonPosition.width}px`
              }
            : undefined
        }>
        {items.length > 0 ? (
          items.map(
            item =>
              optionComponent ? (
                optionComponent(item)
              ) : (
                <Listbox.Option
                  key={item.id}
                  className={({ active }) =>
                    `text-primaryText font-semibold text-sbase relative cursor-pointer select-none ${active ? '' : ''}`
                  }
                  value={item}>
                  {({ selected }) => (
                    <span className={`block truncate py-2 pl-5 pr-4 ${selected ? '' : ''} ${itemStyle}`}>
                      {item.name}
                    </span>
                  )}
                </Listbox.Option>
              )
            // eslint-disable-next-line function-paren-newline
          )
        ) : (
          <div className="py-3 pl-5 pr-4 text-accentText">{emptyStateMessage}</div>
        )}
      </Listbox.Options>
    );

    if (isPortal) {
      return createPortal(
        <div id="root">
          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            {options}
          </Transition>
        </div>,
        document.getElementById('portal-root')
      );
    }

    return (
      <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
        {options}
      </Transition>
    );
  };

  return (
    <div className={`w-full ${wrapperStyles}`}>
      <Listbox value={selectedItem} onChange={setSelectedItem} disabled={disabled}>
        <div className="relative mt-1">
          <Listbox.Button
            disabled={disabled}
            className={twMerge(
              ` 
              relative w-full cursor-default rounded-[4px] bg-white 
              py-4 px-5 text-left focus:outline-none 
            focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white
              focus-visible:ring-offset-2 
              `,
              classnames({ [buttonStyles]: buttonStyles })
            )}
            onClick={e => {
              if (onClick) {
                e.preventDefault();
                onClick();
              }
            }}
            id={id}
            ref={buttonRef}>
            {spanComponent && selectedItem ? (
              spanComponent(selectedItem.id)
            ) : (
              <span className="flex items-center">
                {prefixIcon && <span className="mr-2">{prefixIcon}</span>}
                <span className={`block truncate ${disabled ? 'text-accentText' : 'text-primaryText'} ${spanStyles}`}>
                  {selectedItem ? selectedItem.name : placeholder}
                </span>
              </span>
            )}
            {!hideDropDownIcon && (
              <span
                className={twMerge('pointer-events-none absolute inset-y-0 right-5 flex items-center pr-2', iconStyle)}>
                {trailingIcon ? (
                  trailingIcon
                ) : (
                  <DropDownChevronDownIcon className="h-[8px] w-[14px] text-gray-400" aria-hidden="true" />
                )}
              </span>
            )}
          </Listbox.Button>
          {renderOptions()}
        </div>
      </Listbox>
    </div>
  );
};

export default ListDropDown;
